import { useMemo } from "react";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import type { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import type { ProposalTimelineEntry } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalFeeScheduleCategoriesPath } from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  removedProposalCollectionItems,
  useRemovedProposalCollectionItems,
  type ProposalRemovedCollectionItem,
} from "work/proposals/negotiation/proposal-removed-collection-items";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * The categories a fee schedule no longer has, read out of its own history — see
 * `proposal-removed-collection-items.ts` for why a collection keeps its removed items as rows (#4951).
 * This is the fee schedule's reading of those items: the removal's expected value is the whole category
 * as it stood when it left, in the canonical shape `FeeScheduleCategory.fromJSON` reads, with a masked
 * team member already stripped server-side, so the row can never show more than the viewer is allowed.
 */
export type ProposalFeeScheduleRemovedCategory = {
  /** The category's identity in the negotiation stream. */
  itemId: string;
  /** The category as it stood when it was removed. */
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  /** The removal's place in the stream, so the rows read in the order the categories left. */
  sequence: number;
};

const NO_REMOVED_CATEGORIES: readonly ProposalFeeScheduleRemovedCategory[] = [];

/**
 * The categories whose newest event in `entries` is a removal and whose id is not in `presentItemIds`,
 * oldest removal first.
 */
export function removedProposalFeeScheduleCategories(
  entries: readonly ProposalTimelineEntry[],
  presentItemIds: Iterable<string>,
): ProposalFeeScheduleRemovedCategory[] {
  return removedCategories(removedProposalCollectionItems(entries, proposalFeeScheduleCategoriesPath, presentItemIds));
}

function removedCategories(items: readonly ProposalRemovedCollectionItem[]): ProposalFeeScheduleRemovedCategory[] {
  return items.flatMap((item) => {
    const category = removedCategoryFromItem(item);
    return category ? [{ itemId: item.itemId, category, sequence: item.sequence }] : [];
  });
}

/**
 * The category a removal carries. A payload this build cannot read (an older event without the whole
 * item) yields no row rather than a broken one.
 */
function removedCategoryFromItem(
  item: ProposalRemovedCollectionItem,
): FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined {
  const json = { ...item.value, id: item.value.id ?? item.itemId };
  // `safeParse` rather than the schema's own `isValidJSON`: that one logs every rejection as an error,
  // and an old event this build cannot draw is an absence, not a fault.
  const parsed = FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.safeParse(json);
  if (!parsed.success) return undefined;
  try {
    return FeeScheduleCategory.fromJSON(parsed.data);
  } catch {
    return undefined;
  }
}

/**
 * The removed categories of the fee schedule of `negotiationId`, for a table that already lists the
 * `presentItemIds`.
 */
export function useRemovedProposalFeeScheduleCategories(args: {
  negotiationId?: string;
  viewerScope?: string;
  presentItemIds: readonly string[];
}): readonly ProposalFeeScheduleRemovedCategory[] {
  const items = useRemovedProposalCollectionItems({
    ...args,
    field: ProposalField.FeeSchedule,
    collectionPath: proposalFeeScheduleCategoriesPath,
  });
  return useMemo(() => (items.length ? removedCategories(items) : NO_REMOVED_CATEGORIES), [items]);
}
